import { yupResolver } from '@hookform/resolvers/yup'
import { findIndex, forEach, map } from 'lodash-es'
import { ChangeEvent, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { AlertRatio } from '@/components/AlertRatio'
import { PageTitle } from '@/components'
import { Button, Checkbox, Input, Modal, Textarea } from '@/components/ui'
import { Languages, Memory, MemoryPayload } from '../../../common/models'
import { LocaleDescriptionObj } from '../../../common/models/locale'
import FormGroup from '../../../core/components/FormGroup'
import { FormUploadPreview } from '../../../core/components/FormUploadPreview'
import {
  createMemory,
  deleteMemory,
  getAllMemories,
  updateMemory,
} from '../../../core/services/memories'
import { uploadImage } from '../../../core/services/upload-image'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Icon } from '@/common/components/Icon'

export interface IMemoryValues {
  translations?: IMemoryContent[]
  imageLink?: string
  ordinal?: number
  videoLink?: string
  published?: boolean
}

interface IMemoryContent {
  locale: string
  title: string
  description: string
}

export interface MemoriesCreateModalProps {
  memory?: Memory
  setShowCreateModal: (showEdit: boolean) => void
  setMemories: (memory: Memory[]) => void
  showCreateModal: boolean
  setEditMode: (editMode: boolean) => void
  editMode: boolean
  page: number
  size: number
  pageChange: (e: number) => void
  setTotalElemenets: (elements: number) => void
  allLanguages: Languages[]
}

export const MemoriesCreateModal = (
  props: MemoriesCreateModalProps,
): JSX.Element => {
  const {
    memory,
    setShowCreateModal,
    setMemories,
    showCreateModal,
    setEditMode,
    pageChange,
    editMode,
    page,
    size,
    setTotalElemenets,
    allLanguages,
  } = props
  const [image, setImage] = useState('')
  const [imageName, setImageName] = useState('')
  const [invalidPhoto, setInvalidPhoto] = useState<boolean>(false)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [showDeleteMemoryModal, setShowDeleteMemoryModal] =
    useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [isErrorRatio, setIsErrorRatio] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    const temp = map(allLanguages, (lang) => {
      return {
        locale: lang.locale,
        description: '',
        title: '',
      }
    })
    setValue('translations', temp)
    if (memory && editMode) {
      const temp: IMemoryContent[] = []
      let index = 0
      forEach(memory.translations, (locale) => {
        const keys = Object.keys(memory.translations)
        if (locale !== null) {
          temp.push({
            locale: keys[index],
            description: locale.content,
            title: locale.title,
          })
          index++
        }
      })
      if (!!temp && temp.length > 0) {
        setTimeout(() => {
          map(temp, (trans) => {
            const index = findIndex(
              allLanguages,
              (l) => l.locale === trans.locale,
            )
            if (index !== -1) {
              setValue(`translations.${index}.locale`, trans.locale)
              setValue(`translations.${index}.description`, trans.description)
              setValue(`translations.${index}.title`, trans.title)
            }
          })
        }, 100)
      }
      setValue('imageLink', memory?.imageLink)
      setValue('videoLink', memory?.videoLink)
      setValue('ordinal', memory?.ordinal)
      setValue('published', memory?.published)
      setImage(memory?.imageLink)
      if (memory?.imageLink) {
        const url = new URL(memory.imageLink)
        const i = url?.pathname?.lastIndexOf('/') + 1
        const imageName = url?.pathname?.substring(i)
        setImageName(imageName)
      }
    }

    return () => {
      setImage('')
      setImageName('')
      reset()
    }
  }, [memory, editMode])

  const schema = yup.object().shape({
    imageLink: yup.string(),
    videoLink: yup.string(),
    published: yup.boolean(),
    ordinal: yup.number(),
    translations: yup
      .array()
      .min(1)
      .of(
        yup.object().shape({
          locale: yup.string().nullable().required('Field is required'),
          title: yup.string().required(),
          description: yup.string().required(),
        }),
      )
      .required(),
  })

  const {
    handleSubmit,
    register,
    formState: { errors, dirtyFields },
    reset,
    setValue,
    control,
  } = useForm<IMemoryValues>({
    mode: 'onChange',
    resolver: yupResolver<IMemoryValues>(schema),
    defaultValues: {
      imageLink: '',
      videoLink: '',
      published: false,
      ordinal: 0,
    },
  })

  const handleMemoryDelete = () => {
    // prettier-ignore
    (async () => {
      try {
        if (editMode && memory) {
          const { status } = await deleteMemory(memory?.id)
          const response = await getAllMemories({
            page,
            size,
          })
          if (response.data.totalPages <= page) {
            pageChange(page)
          }
          setTotalElemenets(response.data.totalElements)
          setMemories(response.data.content)
          if (status === 204 && response.status === 200) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Delete Successful')
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
    handleClose()
  }

  const handleDelete = () => {
    setShowDeleteMemoryModal(true)
  }

  const handleClose = () => {
    setInvalidPhoto(false)
    setIsErrorRatio(false)
    setShowCreateModal(false)
    setImage('')
    setImageName('')
    setEditMode(false)
    reset()
  }

  const submit = (values: IMemoryValues) => {
    const localeDescription: LocaleDescriptionObj = {}
    forEach(values.translations, (item) => {
      localeDescription[item.locale] = {
        content: item.description,
        title: item.title,
      }
    })
    const data: MemoryPayload = {
      translations: localeDescription,
      imageLink: values.imageLink,
      ordinal: values.ordinal,
      videoLink: values.videoLink,
      published: values.published,
    }
    if (memory) {
      // prettier-ignore
      (async () => {
        setIsSubmitting(true)
        try {
          const { status } = await updateMemory(memory.id, data)
          const response = await getAllMemories({
            page,
            size,
          })
          setTotalElemenets(response.data.totalElements)
          setMemories(response.data.content)
          if (status === 200 && response.status === 200) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Update Successful')
          }
        } catch (e) {
          if (e instanceof Error) {
            setIsError(true)
            setShowMessageBox(true)
            setMessage(e.message)
            setErrorStatus((e as { response?: { status?: number } })?.response?.status)
          } else {
            console.error(e)
          }
        } finally {
          setIsSubmitting(false)
        }
        handleClose()
      })()
    } else {
      // prettier-ignore
      (async () => {
        setIsSubmitting(true)
        try {
          const { status } = await createMemory(data)
          const response = await getAllMemories({
            page,
            size,
          })
          setTotalElemenets(response.data.totalElements)
          setMemories(response.data.content)
          if (status === 200 && response.status === 200) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Create Successful')
          }
        } catch (e) {
          if (e instanceof Error) {
            setIsError(true)
            setShowMessageBox(true)
            setMessage(e.message)
            setErrorStatus((e as { response?: { status?: number } })?.response?.status)
          } else {
            console.error(e)
          }
        } finally {
          setIsSubmitting(false)
        }
        handleClose()
      })()
    }
  }

  const memoryTitle = memory?.translations
    ? Object.values(memory.translations)[0]?.title
    : undefined

  return (
    <>
      <Modal
        open={showCreateModal}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="lg"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              title="Memories"
              subtitle={
                editMode ? memoryTitle ?? 'Edit Memory' : 'Add New Memory'
              }
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center gap-2">
            {editMode && (
              <Button
                onClick={handleDelete}
                variant="destructive"
                className="mr-auto"
              >
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
            <Button onClick={handleClose} type="button" variant="secondary">
              Cancel
            </Button>
            <Button
              disabled={
                isSubmitting ||
                isUploading ||
                Object.keys(dirtyFields).length === 0 ||
                isErrorRatio
              }
              form="memory-form"
              type="submit"
              variant="primary"
              loading={isSubmitting || isUploading}
            >
              Save
            </Button>
          </div>
        }
      >
        <form
          id="memory-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <div className="flex w-full flex-col gap-2">
            {map(allLanguages, (language, index) => {
              return (
                <div
                  className="flex w-full flex-col gap-3 rounded-[10px] border border-monochrome-120 bg-monochrome-5-50 p-4"
                  key={language.id}
                >
                  <FormGroup label="Language:" wrapperClassName="mb-0">
                    <span className="text-sm font-semibold text-accent-1">
                      {language.name}
                    </span>
                  </FormGroup>
                  <FormGroup
                    label="Title"
                    error={errors?.translations?.[index]?.title?.message}
                  >
                    <Input
                      isInvalid={
                        !!errors?.translations?.[index]?.title?.message
                      }
                      {...register(`translations.${index}.title`)}
                      type="text"
                      placeholder="Enter Title"
                    />
                  </FormGroup>
                  <FormGroup
                    label="Description"
                    error={errors?.translations?.[index]?.description?.message}
                  >
                    <Textarea
                      isInvalid={
                        !!errors?.translations?.[index]?.description?.message
                      }
                      {...register(`translations.${index}.description`)}
                      rows={5}
                      placeholder="Enter Description"
                    />
                  </FormGroup>
                </div>
              )
            })}
          </div>
          <FormGroup label="Ordinal" error={errors?.ordinal?.message}>
            <Input
              isInvalid={!!errors?.ordinal?.message}
              {...register('ordinal')}
              type="number"
              min={0}
              max={100}
              placeholder="Enter Weight"
            />
          </FormGroup>
          <FormGroup label="Youtube URL" error={errors?.videoLink?.message}>
            <Input
              isInvalid={!!errors?.videoLink?.message}
              {...register('videoLink')}
              type="text"
              placeholder="Enter Youtube URL"
            />
          </FormGroup>
          <FormUploadPreview
            control={
              <Input
                multiple={false}
                accept="image/*"
                isInvalid={
                  !!errors?.imageLink?.message || invalidPhoto || isErrorRatio
                }
                name="photo"
                onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                  const file = e?.target?.files?.[0]
                  if (file) {
                    setInvalidPhoto(false)
                    setIsErrorRatio(false)
                    if (file.size > 1_000_000) {
                      setInvalidPhoto(true)
                      setIsError(true)
                      setShowMessageBox(true)
                      setMessage('The photo is too large (1 MB)')
                      return
                    }
                    const data = await uploadImage(
                      file,
                      setIsUploading,
                      setIsError,
                      setShowMessageBox,
                      setMessage,
                      setIsErrorRatio,
                      'MEMORY',
                    )
                    if (data.photoUrl) {
                      setImage(data.photoUrl)
                      setImageName(file.name)
                      setValue('imageLink', data.photoUrl, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  }
                }}
                type="file"
                className="bg-monochrome-5-50"
              />
            }
            imageName={imageName}
            isUploading={isUploading}
            url={image}
            onRemove={() => {
              setImage('')
              setImageName('')
              setValue('imageLink', '')
            }}
            imageStyle={{
              objectFit: 'contain',
            }}
            label="Image (630X360)"
            previewLabel="Image (630X360)"
          />
          {isErrorRatio && <AlertRatio />}
          <FormGroup
            label="Published"
            direction="row"
            labelWrapperClassName="whitespace-nowrap"
          >
            <Controller
              name="published"
              control={control}
              render={({ field }) => (
                <Checkbox
                  className="border-monochrome-1"
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                />
              )}
            />
          </FormGroup>
        </form>
      </Modal>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteMemoryModal}
        setDeleteConfirmationModal={setShowDeleteMemoryModal}
        deleteTitle={'Memory'}
        deleteFunc={handleMemoryDelete}
        deletedItem={memory?.translations?.[0]?.title}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
